import { useIntersection } from 'react-use';
import { type FocusEvent, type FormEvent, useCallback, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next/pages';
import { CountryCode, type InputContact, type InputContactPreferredLanguage, MarketCountryCode } from '@pkgs/api';
import { useSession } from '@ev-tech/user-account-modules';
import { Textarea } from '@ev-tech/ui-core-components/Textarea';
import { Skeleton } from '@ev-tech/ui-core-components/Skeleton';
import { Overline } from '@ev-tech/ui-core-components/Overline';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import { Button } from '@ev-tech/ui-core-components/Button';

import { trackGTMLeadEvent } from '@/utils/tracking';
import { isTouchDevice } from '@/utils/isTouchDevice';
import { extractFormData } from '@/utils/formUtils';
import { getLocalizedCountryList } from '@/utils/countries';
import { buildContactFormMessage, mapLeadFormDataToRequestData } from '@/utils/contactForm';
import { PageVariant } from '@/types/expose';
import { sendContactFormService } from '@/services/sendContactFormData';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useSettings } from '@/hooks/useSettings';
import { useExposeTracking } from '@/hooks/useExposeTracking';
import { useExposePageProps } from '@/hooks/useExposePageProps';
import { useListing } from '@/hooks/queries/useListing';

import { type ContactFormData } from './ContactForm.types';
import { ShopConsent } from './components/ShopConsent';
import { PrivateOfficeConsent } from './components/PrivateOfficeConsent';
import { PrivacyStatement } from './components/PrivacyStatement';
import { LastNameInput } from './components/LastNameInput';
import { type ContactFormStatus } from './components/FormStatusDisplay';
import { FirstNameInput } from './components/FirstNameInput';
import { FinanceConsent } from './components/FinanceConsent';
import { EmailInput } from './components/EmailInput';
import { CountryConsent } from './components/CountryConsent';
import { ConsentLabel } from './components/ConsentLabel';
import { Benefits } from './components/Benefits';

import styles from './ContactForm.module.css';

const FormStatusDisplay = dynamic(() => import('./components/FormStatusDisplay').then((mod) => mod.FormStatusDisplay), {
  loading: () => <Skeleton className={styles.formStatusDisplayPlaceholder} />,
});
const PhoneInput = dynamic(() => import('./components/PhoneInput').then((mod) => mod.PhoneInput), {
  loading: () => <Skeleton className={styles.phoneInputPlaceholder} />,
});

type ContactFormProps = {
  isModal?: boolean;
  buttonRef?: React.RefObject<HTMLButtonElement>;
  inputGridRef?: React.LegacyRef<HTMLFieldSetElement>;
};

export const ContactForm = ({ isModal, buttonRef, inputGridRef }: ContactFormProps) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);
  const { countryCode, language } = useSettings();
  const { pageVariant, promotedBy, pageTrackingTypes } = useExposePageProps();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [observedFormElement, setObservedFormElement] = useState<HTMLFormElement | null>(null);
  const [status, setStatus] = useState<ContactFormStatus>('idle');
  const [isMessageFieldFocused, setIsMessageFieldFocused] = useState(false);
  const requestData = useRef<InputContact | null>(null);
  const countryConsentRef = useRef<HTMLDivElement>(null);
  const privateOfficeConsentRef = useRef<HTMLDivElement>(null);
  const shopConsentRef = useRef<HTMLDivElement>(null);
  const financeConsentRef = useRef<HTMLDivElement>(null);
  const consentRef = useRef<HTMLDivElement>(null);
  const { trackExposeLeadEvent } = useExposeTracking();
  const { session } = useSession();
  const {
    data: { listing },
  } = useListing();

  const consentVisibility = {
    showShopConsent: !!listing?.shopConsent,
    showCountryConsent: !!listing?.countryConsent,
    showFinanceConsent: !!listing?.financeConsent,
  };

  const observedFormRef = useMemo(() => ({ current: observedFormElement }), [observedFormElement]);
  const setFormRef = useCallback((node: HTMLFormElement | null) => {
    formRef.current = node;
    setObservedFormElement(node);
  }, []);

  const formIntersection = useIntersection(observedFormRef, {
    rootMargin: '100px 0px 0px 0px',
    threshold: 0,
  });

  const initialCountry = countryCode === MarketCountryCode['private-office'] ? CountryCode.DE : countryCode;

  const handleFormFocus = useCallback((event: FocusEvent<HTMLFormElement>) => {
    if (event.target instanceof HTMLTextAreaElement && event.target.name === 'message') {
      setIsMessageFieldFocused(true);
    }
  }, []);

  // Closing a keyboard by clicking "done" on mobile does not trigger a blur event due to missing event listeners in react https://github.com/facebook/react/issues/28492
  const handleFormBlur = useCallback((event: FocusEvent<HTMLFormElement>) => {
    if (event.target instanceof HTMLTextAreaElement && event.target.name === 'message') {
      setIsMessageFieldFocused(false);
    }
  }, []);

  const hasPrivateOfficeConsentCheckbox = countryCode === MarketCountryCode['private-office'] && Boolean(listing?.poConsent);
  const hasConsentCheckbox = Object.values(consentVisibility).some(Boolean) || hasPrivateOfficeConsentCheckbox;
  const defaultMessage = t('search.contact.form.label.yourMessageDefault');
  const searchDetailsIntro = t('search.contact.form.label.searchDetailsIntro');

  const messageDefault = useMemo(() => buildContactFormMessage(defaultMessage, searchDetailsIntro), [defaultMessage, searchDetailsIntro]);

  if (!listing) return null;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // check if form is valid and scroll to first invalid input
    if (!formRef.current?.checkValidity()) {
      const firstInvalidInput = formRef.current?.querySelector<HTMLInputElement>('input:invalid');
      firstInvalidInput?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      firstInvalidInput?.focus({ preventScroll: true });
      return;
    }

    const formData = extractFormData<ContactFormData>(formRef.current);

    // map country code to country name
    if (formData.country) {
      formData.country = getLocalizedCountryList(language).find((c) => c.code === formData.country)?.name ?? formData.country;
    }
    if (formData.marketingConsent) {
      formData.marketingConsentText = countryConsentRef.current?.innerHTML;
    }
    if (formData.privateOfficeConsent) {
      formData.privateOfficeConsentText = privateOfficeConsentRef.current?.innerHTML;
    }
    if (formData.shopConsent) {
      formData.shopConsentText = shopConsentRef.current?.innerHTML;
    }
    if (formData.financeConsent) {
      formData.financeConsentText = financeConsentRef.current?.innerHTML;
    }

    formData.consentText = consentRef.current?.innerText;

    requestData.current = mapLeadFormDataToRequestData({
      formData,
      preferredLanguage: language.toUpperCase() as InputContactPreferredLanguage,
      marketCountry: countryCode,
    });

    if (promotedBy) {
      requestData.current.promotingAgentID = promotedBy;
    }

    setStatus('processing');
  };

  const sendFormRequest = async (solution: string) => {
    if (status !== 'processing' || !requestData.current) return;

    const result = await sendContactFormService(listing.id, requestData.current, solution);

    setStatus(result.success ? 'success' : 'error');

    if (result.success) {
      trackExposeLeadEvent({
        requestData: requestData.current,
        source: isModal ? 'request-expose-modal-form' : 'request-expose-embedded-form',
      });
      trackGTMLeadEvent({
        requestData: requestData.current,
        listing,
        pageTrackingTypes,
        sessionEmailAddress: session?.user.emailAddresses[0]?.emailAddress,
      });
    }
  };

  return (
    <>
      {status === 'idle' ? (
        <form
          className={styles.form}
          data-testid="request-expose-form"
          data-add-bottom-padding={Boolean(isModal && isMessageFieldFocused && isTouchDevice())}
          ref={setFormRef}
          onSubmit={handleSubmit}
          onFocus={handleFormFocus}
          onBlur={handleFormBlur}
          noValidate
        >
          <div className={styles.titleWrapper}>
            <Overline
              text={
                pageVariant === PageVariant.PRIVATE_OFFICE_PUBLIC
                  ? t('search.contact.form.text.overline.po')
                  : t('search.contact.form.text.overline')
              }
            />
            <Headline className={styles.headline} size="h4" label={t('search.contact.form.text.getMoreInfo')} />
          </div>
          <fieldset className={styles.inputGrid} ref={inputGridRef}>
            <FirstNameInput />
            <LastNameInput />
            <EmailInput />
            {/* Only render the phone input if the form is intersecting, to avoid having all the options in the DOM when the form is not visible */}
            {formIntersection?.isIntersecting || isModal ? (
              <PhoneInput initialCountry={initialCountry} isInModal={isModal} />
            ) : (
              <Skeleton className={styles.phoneInputPlaceholder} />
            )}
          </fieldset>

          <Textarea
            key={messageDefault}
            label={t('search.contact.form.label.yourMessage')}
            name="message"
            id="contactFormMessage"
            placeholder={t('search.contact.form.label.yourMessage.placeHolder')}
            defaultValue={messageDefault}
          />
          <div className={styles.consentLabelWrapper}>
            <ConsentLabel showsPrivacyStatement={hasConsentCheckbox} countryAlpha2={listing.countryAlpha2} consentRef={consentRef} />
            {consentVisibility.showShopConsent && <ShopConsent shopConsentRef={shopConsentRef} listing={listing} />}
            {consentVisibility.showCountryConsent && <CountryConsent countryConsentRef={countryConsentRef} listing={listing} />}
            {consentVisibility.showFinanceConsent && <FinanceConsent financeConsentRef={financeConsentRef} listing={listing} />}
            {countryCode === MarketCountryCode['private-office'] && listing.poConsent && (
              <PrivateOfficeConsent privateOfficeConsentRef={privateOfficeConsentRef} listing={listing} />
            )}
          </div>
          <Button
            ref={buttonRef}
            className={styles.button}
            size="medium"
            type="submit"
            label={t('search.contact.form.button.send')}
            testId="search-components_expose-contact-form_submit-button"
          />
          {!hasConsentCheckbox && <PrivacyStatement countryAlpha2={listing.countryAlpha2} />}
          <Benefits />
        </form>
      ) : (
        <FormStatusDisplay
          status={status}
          isModal={isModal}
          onRetry={() => setStatus('idle')}
          onSolution={sendFormRequest}
          email={requestData.current?.email}
        />
      )}
    </>
  );
};
